/** @format */

import React, {useCallback, useEffect, useState} from 'react';
import PaperedTable from '../../components/Table/PaperedTable';
import GridItem from '../../components/Grid/GridItem';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import GridContainer from '../../components/Grid/GridContainer';
import {FormGeneralHelper} from '../../helpers/FormGeneral';
import {configDefault} from '../../core/config';
import {ItemLoaders} from '../../core/loaders/ItemLoaders';
import {platformResellerConstants} from '../../modules/platformreseller/actions';
import {PlatformResellerCollectionInterface} from '../../interfaces/collections/PlatformResellerCollectionInterface';
import {useDispatch} from 'react-redux';
import {TableBody, TableCell, TableFooter, TableHead, TableRow, withStyles} from '@material-ui/core';
import EditButton from '../../components/Buttons/EditButton';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import Filter from '../../interfaces/forms/Filter';
import Order from '../../interfaces/forms/Order';
import {AlertDialog} from '../lib/dialog/AlertDialog';
import DeleteButton from '../../components/Buttons/DeleteButton';
import {tariffConstants} from '../../modules/tariffs/actions';
import {NotificationHelper} from '../../helpers/NotificationHelper';
import oplAppStyle from "../../assets/themes/OPL/jss/App/OplAppStyle";
import {PlatformResellerLoader} from "../../modules/platformreseller/PlatformResellerLoader";
import PlatformResellerInterface from "../../interfaces/platform/PlatformResellerInterface";
import {FilterReducerFieldName} from "../../modules/filter/FilterReducers";
import {FilterListReducerFieldName} from "../../modules/filter/FilterListReducers";
import {
    ServiceUseType,
    ServiceUseTypeMap,
    useOperatorType,
    useServiceUseType
} from "../../modules/lib/hooks/useOperatorType";
import {FeatureTypes} from "../../modules/features/FeaturesTypes";
import Pager from "../../components/Adescom/PagerRange";

interface ServicesActivationListInterface {
    classes:any;
    addCallback: () => void;
    editCallback: (el: any) => void;
}

interface UssdSmsCommunication {
    id?: number;
    mainname: string;
    useType: number;
    platformResellerId: number;
    ussdCode: string | null;
    smsCode: string | null;
    positiveResponse: string | null;
    negativeResponse: string | null;
    ussdDeactivateCode: string | null;
    ussdCheckCode: string | null;
    smsDeactivateCode: string | null;
    smsCheckCode: string | null;
    activationCost: string | null;
    noBalanceResponse: string | null;
}

const ServicesActivationList = (props: ServicesActivationListInterface): React.ReactElement => {
    const dispatch = useDispatch();
    const [services, setServices] = useState<any>(null);
    const [filters, setFilters] = useState<Array<Filter>>([]);
    const [orders, setOrders] = useState<Array<Order>>([]);
    const [selectedIdToDelete, setSelectedIdToDelete] = useState<number | null>(null);
    const [platformResellers, setPlatformResellers] = useState<any>(null);
    const operatorTypeHook = useServiceUseType();

    const tableCellRoot = { root: props.classes.tableCellRoot };
    const tableStickyHeader = { root: props.classes.tableStickyHeader };
    const tableRowRoot = { root: props.classes.tableRowRoot };
    const tableStickyCommand = {root: props.classes.tableStickyCommand};
    const tableControlStickyHeader = {root: props.classes.tableControlStickyHeader};

    const loadServices = useCallback(
        (uri?: string | null): void => {
            if (!uri) {
                uri = FormGeneralHelper.getPath('/ussd_sms_communications');
            } else {
                // Quick fix, We need to put "?" before "&" because we do not use filters here, so uri does not have it. When we dont have "?" in uri system does not recognise path
                uri = uri.replace(/&/g, "?&");
            }

            const loader = new ItemLoaders(dispatch);
            loader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                platformResellerConstants.PLATFORM_RESELLER_LIST,
                [...filters, {name: 'exists[ownerCodeId]', value: false}],
                orders,
                (data: any) => {
                    setServices(data);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch, filters, orders]
    );

    const updateOrders = (orders: Array<Order>): void => {
        setOrders(orders);
    };
    const updateFilters = (filters: Array<Filter>): void => {
        setFilters(filters);
    };

    const clearFilter = (): void => {
        setFilters([]);
        setOrders([]);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };

    const makeDelete = (params: { id: number }): void => {
        setSelectedIdToDelete(params.id);
    };

    const confirmDelete = (): void => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.deleteCall(
            FormGeneralHelper.getByUri(
                FormGeneralHelper.getPath(`/ussd_sms_communications/${selectedIdToDelete}`, configDefault.PLATFORM_API_PREFIX)
            ),
            tariffConstants.DELETE,
            (data: any) => {
                setSelectedIdToDelete(null);
                console.log(data);
                if (!data) {
                    NotificationHelper.notifyError('services_activation:notify.delete_error', dispatch);
                } else {
                    NotificationHelper.notifySuccess('services_activation:notify.delete_success', dispatch);
                    loadServices();
                }
            },
            { dispatchAfterCallback: false }
        );
    };

    const loadPlatformResellers = useCallback(
        (): void => {
            const itemLoaders = new ItemLoaders(dispatch);
            const platformResellersLoader = new PlatformResellerLoader(itemLoaders);

            platformResellersLoader.loadPlatformResellers({
                callback: () => {
                    setPlatformResellers(platformResellersLoader.getPlatformResellersHeaderList());
                },
                filters: [{ name: 'deleted', value: 0 }],
            });
        },
        [dispatch]
    );

    const getPlatformResellerNameById = (platformResellerId: number):string => {
        let name = '';
        if(platformResellers) {
            platformResellers.map((el: {name: 'string',value: number}) => {
                if(platformResellerId === el.value) {
                    name = el.name;
                }
            })
        }
        return name;
    }

    const getOperatorType = (type: number): string => {
        switch(type) {
            case ServiceUseTypeMap.PREPAID:
                return ServiceUseType.PREPAID;
            case ServiceUseTypeMap.POSTPAID:
                return ServiceUseType.POSTPAID;
            case ServiceUseTypeMap.BOTH:
                return ServiceUseType.BOTH;
            default:
                return '';
        }
    }

    useEffect(() => {
        loadServices();
    }, [loadServices]);

    useEffect(() => {
        if(!platformResellers) {
            loadPlatformResellers();
        }
    }, [loadPlatformResellers, platformResellers])

    return (
        <GridContainer spacing={2}>
            <GridItem xs={12}>
                {props.addCallback && (
                    <CustomTextButtom
                        color="primary"
                        label={'services_activation:form.add_new'}
                        callback={props.addCallback}
                        callbackParam={{}}
                        size={'lg'}
                    />
                )}
            </GridItem>
            <GridItem xs={12}>
                {services && platformResellers && (
                    <PaperedTable
                        beforeTableContent={
                            <GridContainer>
                                <GridItem xs={12} style={{ textAlign: 'left' }}>
                                    {(filters.length > 0 || orders.length > 0) && (
                                        <CustomTextButtom
                                            callback={clearFilter}
                                            callbackParam={{}}
                                            color="primary"
                                            label="general:common.clear"
                                            size="lg"
                                        />
                                    )}
                                </GridItem>
                            </GridContainer>
                        }
                    >
                        <TableHead>
                            <TableRow classes={tableRowRoot}>
                                <TableCell classes={tableStickyCommand}>
                                    <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="pricelists:h.name"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="mainname"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="services_activation:form.select_partner"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="platformResellerId"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="select"
                                        selectComponent="headerCheckBoxSimpleList"
                                        elementList={platformResellers}
                                        filters={filters}
                                        sort={orders}
                                        searchActionCallback={loadServices}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="services_activation:form.select_type"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="useType"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="select"
                                        selectComponent="headerCheckBoxSimpleList"
                                        elementList={operatorTypeHook}
                                        filters={filters}
                                        sort={orders}
                                        searchActionCallback={loadServices}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="services_activation:form.ussd_activate_code"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="ussdCode"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="services_activation:form.ussd_deactivate_code"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="ussdDeactivateCode"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="services_activation:form.ussd_check_code"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="ussdCheckCode"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="services_activation:form.sms_activate_code"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="smsCode"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="services_activation:form.sms_deactivate_code"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="smsDeactivateCode"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="services_activation:form.sms_check_code"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="smsCheckCode"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="services_activation:form.sms_positive_default_response"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="positiveResponse"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="services_activation:form.sms_negative_response"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="negativeResponse"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="services_activation:form.zero_balance_response"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="noBalanceResponse"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader}>
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="services_activation:form.activation_cost"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="activationCost"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {services &&
                                services['hydra:member'].map((el: UssdSmsCommunication) => {
                                    return (
                                        <TableRow key={el.id}>
                                            <TableCell classes={tableControlStickyHeader}>
                                                <EditButton callback={(): void => props.editCallback(el)}
                                                            callbackParam={{}}/>
                                                <DeleteButton callback={makeDelete} callbackParam={{id: el.id}}/>
                                            </TableCell>
                                            <TableCell classes={tableCellRoot}>{el.mainname}</TableCell>
                                            <TableCell classes={tableCellRoot}>{getPlatformResellerNameById(el.platformResellerId)}</TableCell>
                                            <TableCell classes={tableCellRoot}>{getOperatorType(el.useType)}</TableCell>
                                            <TableCell classes={tableCellRoot}>{el.ussdCode}</TableCell>
                                            <TableCell classes={tableCellRoot}>{el.ussdDeactivateCode}</TableCell>
                                            <TableCell classes={tableCellRoot}>{el.ussdCheckCode}</TableCell>
                                            <TableCell classes={tableCellRoot}>{el.smsCode}</TableCell>
                                            <TableCell classes={tableCellRoot}>{el.smsDeactivateCode}</TableCell>
                                            <TableCell classes={tableCellRoot}>{el.smsCheckCode}</TableCell>
                                            <TableCell classes={tableCellRoot}>{el.positiveResponse}</TableCell>
                                            <TableCell classes={tableCellRoot}>{el.negativeResponse}</TableCell>
                                            <TableCell classes={tableCellRoot}>{el.noBalanceResponse}</TableCell>
                                            <TableCell classes={tableCellRoot}>{el.activationCost}</TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={10}>
                                    {services && services['hydra:view'] && (
                                        <Pager callback={loadServices} views={services}/>
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </PaperedTable>
                )}
            </GridItem>
            <GridItem xs={12}>
                <AlertDialog
                    open={!!selectedIdToDelete}
                    titleDialog="notifications:h.delete_confirmation"
                    displayMessage={'services_activation:notify.confirm_ussd_sms_delete'}
                    closeCallback={(): void => {
                        setSelectedIdToDelete(null);
                    }}
                    confirmCallback={confirmDelete}
                />
            </GridItem>
        </GridContainer>
    );
};

export default ServicesActivationList;
